import {
  Box,
  Dialog,
  Typography,
  DialogActions,
  DialogContent,
  Tooltip,
  styled,
} from '@mui/material'
import { useMemo } from 'react'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { MyWbsItemsTaskTabSearchCondition } from '../../hooks/useSearchCondition'
import { intl } from '../../../../../i18n'
import { Button } from '../../../../components/buttons'
import { WbsItemStatus } from '../../../../../domain/entity/WbsItemEntity'
import { useOnSingleValueChangedGenerator } from '../../hooks/useOnSingleValueChangedGenerator'
import { WBS_ITEM_SEARCH_COUNT_LIMIT } from '../../../WbsItemSearch/wbsItemSearch'
import CountSearchResult from '../../../../components/dialogs/WbsItemSearchDialog/CountSearchResultDialog'
import SearchConditionSelectBoxCheckmarks from '../../../../components/search-forms/search-panel/SearchConditionSelectBoxCheckmarks'
import SearchConditionDate from '../../../../components/search-forms/search-panel/SearchConditionDate'

type Props = {
  open: boolean
  onClose: () => void
  searchCondition: MyWbsItemsTaskTabSearchCondition
  onChangeCondition: (
    updated: Partial<MyWbsItemsTaskTabSearchCondition>
  ) => void
  onEnter: (() => void) | undefined
  resetCondition: () => void
  search: () => void
  filteringInput: string
  setFilteringInput: (newValue: string) => void
  taskCount: number | undefined
}
const MyWbsItemsTaskTabSearchConditionDialog = ({
  open,
  onClose,
  onChangeCondition,
  searchCondition,
  resetCondition,
  search,
  taskCount,
}: Props) => {
  const { onChangeValueGenerator } =
    useOnSingleValueChangedGenerator(onChangeCondition)

  const statusOptions = useMemo(() => {
    return Object.keys(WbsItemStatus).map(k => ({
      name: k,
      value: k,
    }))
  }, [])

  const normalizeValue = (
    newValue: string | string[] | undefined
  ): string[] | undefined => {
    if (Array.isArray(newValue)) return newValue
    if (newValue) return [newValue]
    return undefined
  }

  const isSearchable = useMemo(() => {
    if (taskCount === undefined) {
      return false
    }
    return taskCount <= WBS_ITEM_SEARCH_COUNT_LIMIT
  }, [taskCount])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '& .MuiPaper-root': { maxWidth: '800px' },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            height: '350px',
            gap: '10px',
          }}
        >
          <SearchConditionSelectBoxCheckmarks
            value={searchCondition.status}
            options={statusOptions}
            onChange={(newValue: string | string[] | undefined) => {
              const value = normalizeValue(newValue)
              onChangeValueGenerator('status')(
                value?.map(v => v as WbsItemStatus)
              )
            }}
            title={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.status',
            })}
          />
          <SearchConditionDate
            value={searchCondition.scheduledStartDate}
            title={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledStartDate',
            })}
            onChange={onChangeValueGenerator('scheduledStartDate')}
            isBlankSearchEnabled={false}
          />
          <SearchConditionDate
            value={searchCondition.scheduledEndDate}
            title={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledEndDate',
            })}
            onChange={onChangeValueGenerator('scheduledEndDate')}
            isBlankSearchEnabled={false}
          />
          <SearchConditionDate
            value={searchCondition.actualStartDate}
            title={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualStartDate',
            })}
            onChange={onChangeValueGenerator('actualStartDate')}
            isBlankSearchEnabled={false}
          />
          <SearchConditionDate
            value={searchCondition.actualEndDate}
            title={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualEndDate',
            })}
            onChange={onChangeValueGenerator('actualEndDate')}
            isBlankSearchEnabled={false}
          />
        </Box>
      </DialogContent>
      <StyledDialogActions>
        <CountSearchResult count={taskCount} />
        <DialogButtons>
          <Button
            size="m"
            colorPattern="monotone"
            variant="outlined"
            onClick={resetCondition}
          >
            <ClearRoundedIcon />
            <Typography>
              {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.reset' })}
            </Typography>
          </Button>
          <Tooltip
            title={
              !isSearchable
                ? intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.tooltip.searchLimit',
                  })
                : ''
            }
          >
            <span>
              <Button
                size="m"
                colorPattern="skyBlue"
                variant="filled"
                onClick={search}
                disabled={!isSearchable}
              >
                <Typography>
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.search',
                  })}
                </Typography>
              </Button>
            </span>
          </Tooltip>
        </DialogButtons>
      </StyledDialogActions>
    </Dialog>
  )
}

const StyledDialogActions = styled(DialogActions)({
  padding: '20px 10px',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '8px',
})
const DialogButtons = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
})

export default MyWbsItemsTaskTabSearchConditionDialog
